
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, FileText } from 'lucide-react';
import { ReportData } from './types';
import { Pagination } from '@/components/ui/pagination';

interface ReportsTabProps {
  savedReports: ReportData[];
  handleDownload: (report: ReportData) => void;
}

const ITEMS_PER_PAGE = 3;

const ReportsTab: React.FC<ReportsTabProps> = ({ savedReports, handleDownload }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (!savedReports || savedReports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-xl font-medium text-gray-700 dark:text-white mb-2">No Reports Found</h3>
        <p className="text-gray-500 dark:text-gray-400 text-center mb-6">
          You don't have any saved reports yet. Generate a new report to get started.
        </p>
        <Button variant="outline" className="bg-white dark:bg-transparent border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800">
          <FileText className="h-4 w-4 mr-2" />
          Generate New Report
        </Button>
      </div>
    );
  }

  const totalPages = Math.ceil(savedReports.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, savedReports.length);
  const currentReports = savedReports.slice(startIndex, endIndex);
  
  return (
    <div className="space-y-6">
      {currentReports && currentReports.map((report) => (
        <div key={report.id} className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0A1428] shadow-sm hover:shadow-md transition-shadow overflow-hidden">
          <div className="p-4">
            <div className="flex items-center mb-4">
              <div className="rounded-full bg-blue-100 dark:bg-blue-900/20 p-3 mr-4">
                {report.fileIcon}
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-800 dark:text-white text-lg">{report.title}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Last generated: {report.date}</div>
              </div>
              <Badge variant="outline" className="mr-4 text-xs bg-transparent border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                {report.fileType}
              </Badge>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/50"
                onClick={() => handleDownload(report)}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="bg-gray-50 dark:bg-[#0D1730] rounded-lg p-4">
              <div className="text-center mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">Report Preview</div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Total: {report.dataPreview.total}</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">Cost: {report.dataPreview.cost}</span>
              </div>
              <div className="flex justify-between">
                <span className={`text-sm ${report.dataPreview.trendType === 'positive' ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'}`}>
                  Trend: {report.dataPreview.trend}
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Key Insight: {report.dataPreview.insight}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-8">
          <Button variant="outline" className="bg-white dark:bg-transparent border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800">
            <FileText className="h-4 w-4 mr-2" />
            Generate New Report
          </Button>
          
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
      
      {totalPages <= 1 && (
        <div className="flex justify-start mt-8">
          <Button variant="outline" className="bg-white dark:bg-transparent border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800">
            <FileText className="h-4 w-4 mr-2" />
            Generate New Report
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
